// src/pages/TenantsPage.jsx
import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://mrm.lemda.lk/api';

// SVG placeholder for images
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
    <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#e5e7eb"/><stop offset="100%" stop-color="#cbd5e1"/></linearGradient></defs>
    <rect width="1200" height="800" fill="url(#g)"/>
    <g fill="#475569" font-family="Arial, Helvetica, sans-serif" text-anchor="middle">
      <text x="600" y="390" font-size="28" font-weight="700">Property image</text>
      <text x="600" y="425" font-size="14" opacity="0.85">Not available</text>
    </g>
  </svg>`);

const fmtGBP = (v, text) =>
  text ? text : v == null ? 'POA' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);

export default function TenantsPage() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  const heroImage =
    'https://images.unsplash.com/photo-1598928506311-4c8b8820f9a3?q=80&w=2069&auto=format&fit=crop'; // warm, welcoming interior

  // fetch featured rental properties
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const qs = new URLSearchParams({
          types: 'rent',
          status: 'active',
          per_page: '6',
          sort: 'newest',
        });
        const res = await fetch(`${API_BASE}/properties.php?${qs.toString()}`);
        const json = await res.json().catch(() => ({}));
        if (!cancel && json?.ok) setRentals(Array.isArray(json.data) ? json.data : []);
      } catch {
        // silent fail -> show empty state
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const services = [
    {
      icon: '👤',
      title: 'Tenant Portal',
      description:
        'View your tenancy details, documents and payment history in one secure place.',
      href: '/portal',
      cta: 'Open portal',
    },
    {
      icon: '💳',
      title: 'Online Payments',
      description:
        'Pay rent securely online. Set up one-off or recurring payments in seconds.',
      href: '/tenants/payments',
      cta: 'Pay rent',
    },
    {
      icon: '🛠️',
      title: 'Maintenance Requests',
      description:
        'Report an issue 24/7 and track progress. Urgent problems escalated immediately.',
      href: '/repairs',
      cta: 'Report a repair',
    },
    {
      icon: '🎧',
      title: 'Friendly Support',
      description:
        'Our dedicated team is here to help throughout your tenancy — no jargon.',
      href: '/contact',
      cta: 'Contact support',
    },
  ];

  const steps = [
    { n: 1, t: 'Register & search', d: 'Tell us what you’re after. Get alerts for new homes 24 hours before portals.' },
    { n: 2, t: 'Viewings', d: 'In-person or virtual — we’ll answer your questions and gather feedback.' },
    { n: 3, t: 'Apply & reference', d: 'Simple online referencing and Right to Rent checks.' },
    { n: 4, t: 'Tenancy setup', d: 'E-sign agreements, deposit protection & first rent arranged.' },
    { n: 5, t: 'Move in', d: 'Inventory, keys, meter readings — we’ll guide you through each step.' },
    { n: 6, t: 'Support & renewals', d: 'Maintenance handled quickly. Options to renew or move when ready.' },
  ];

  return (
    <>
      <style>{`
        /* ===== Tenants Page (namespaced) ===== */
        .mrm-tenant { --b:#0ea5e9; --b2:#1e3a8a; --ink:#0f172a; --muted:#475569; --bg:#f8fafc; --paper:#fff; --line:#e5e7eb; color:var(--ink); font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; }
        .mrm-tenant a { color: var(--b2); text-decoration: none; }
        .mrm-tenant a:hover { text-decoration: underline; }
        .mrm-tenant__container { max-width:1200px; margin:0 auto; padding: 1rem; }

        /* HERO */
        .mrm-tenant__hero{
          position:relative; color:#fff; min-height:66vh; display:grid; align-items:center; isolation:isolate;
          background-image:
            linear-gradient(180deg, rgba(2,6,23,.25) 0%, rgba(2,6,23,.65) 100%),
            var(--hero);
          background-size:cover; background-position:center;
        }
        .mrm-tenant__crumb{ opacity:.9; margin-bottom:.5rem; }
        .mrm-tenant__title{ margin:.25rem 0; font-weight:900; line-height:1.05; font-size: clamp(1.8rem, 4.2vw, 3rem); text-shadow:0 2px 18px rgba(0,0,0,.25); }
        .mrm-tenant__lede{ max-width: 820px; font-size: clamp(1rem, 2vw, 1.1rem); opacity:.95; }
        .mrm-tenant__cta{ display:flex; gap:.8rem; flex-wrap:wrap; margin-top:1.25rem; }
        .mrm-tenant__btn{
          display:inline-flex; align-items:center; justify-content:center; gap:.5rem; font-weight:900; border-radius:999px; padding:.9rem 1.2rem; cursor:pointer; border:1px solid rgba(255,255,255,.7); color:#fff; background:transparent; transition:.2s transform,.25s box-shadow,.2s background;
        }
        .mrm-tenant__btn--primary{ background: var(--b); border-color: var(--b); }
        .mrm-tenant__btn:hover{ transform: translateY(-1px); box-shadow:0 10px 22px rgba(0,0,0,.25); }
        .mrm-tenant__chips{ display:flex; gap:.5rem; flex-wrap:wrap; margin-top:1rem; }
        .mrm-tenant__chip{ background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.3); color:#fff; padding:.4rem .6rem; border-radius:10px; font-weight:800; font-size:.84rem; }

        /* SECTIONS */
        .mrm-tenant__section{ padding: clamp(3rem, 6vw, 5.5rem) 0; background: var(--bg); }
        .mrm-tenant__section--alt{ background:#fff; }
        .mrm-tenant__heading{ margin:0; font-weight:900; font-size: clamp(1.4rem, 3vw, 2rem); color: var(--b2); }
        .mrm-tenant__sub{ margin:.35rem 0 1rem; color: var(--muted); max-width: 900px; }

        /* SERVICES */
        .mrm-tenant__grid{ display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width: 760px){ .mrm-tenant__grid{ grid-template-columns:1fr 1fr; } }
        @media (min-width: 1100px){ .mrm-tenant__grid{ grid-template-columns:1fr 1fr 1fr 1fr; } }
        .mrm-tenant__card{ background:#fff; border:1px solid var(--line); border-radius:14px; box-shadow:0 10px 22px rgba(2,6,23,.06); padding:1rem; height:100%; display:grid; gap:.55rem; }
        .mrm-tenant__icon{ width:44px; height:44px; border-radius:12px; display:grid; place-items:center; background:#cffafe; color:#0b3aa4; font-weight:900; font-size:1.15rem; }
        .mrm-tenant__card h3{ margin:.2rem 0 0; font-size:1rem; font-weight:900; }
        .mrm-tenant__card p{ margin:0; color:#475569; font-size:.95rem; line-height:1.55; }
        .mrm-tenant__card a.cta{ display:inline-block; margin-top:.35rem; font-weight:800; }

        /* STEPS */
        .mrm-tenant__steps{ display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width: 760px){ .mrm-tenant__steps{ grid-template-columns:1fr 1fr 1fr; } }
        .mrm-tenant__step{ background:#fff; border:1px solid var(--line); border-radius:14px; padding:1rem; display:flex; gap:.85rem; }
        .mrm-tenant__num{ flex:0 0 auto; width:36px; height:36px; border-radius:50%; background:var(--b2); color:#fff; font-weight:900; display:grid; place-items:center; }
        .mrm-tenant__step h4{ margin:.1rem 0 .1rem; font-size:1rem; font-weight:900; }
        .mrm-tenant__step p{ margin:0; color:#475569; font-size:.95rem; }

        /* INFO BAND */
        .mrm-tenant__band{ color:#fff; background: linear-gradient(135deg, var(--b2), var(--b)); border-radius:20px; padding: clamp(1.2rem, 4vw, 2rem); }
        .mrm-tenant__band h3{ margin:.2rem 0; font-size: clamp(1.2rem, 2.4vw, 1.6rem); font-weight:900; }
        .mrm-tenant__band p{ margin:.2rem 0 1rem; opacity:.95; }
        .mrm-tenant__row{ display:flex; gap:.8rem; flex-wrap:wrap; align-items:center; }

        /* FEATURED RENTALS */
        .mrm-tenant__props{ display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width:760px){ .mrm-tenant__props{ grid-template-columns:1fr 1fr; } }
        @media (min-width:1100px){ .mrm-tenant__props{ grid-template-columns:1fr 1fr 1fr; } }
        .mrm-tenant__prop{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; display:flex; flex-direction:column; }
        .mrm-tenant__thumb{ position:relative; height:180px; background:#e5e7eb; }
        .mrm-tenant__thumb img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
        .mrm-tenant__pill{ position:absolute; top:10px; left:10px; background:rgba(0,0,0,.6); color:#fff; border-radius:999px; padding:.25rem .5rem; font-size:.75rem; font-weight:800; }
        .mrm-tenant__body{ padding:.8rem .9rem; display:grid; gap:.35rem; }
        .mrm-tenant__h{ margin:0; font-weight:800; font-size:1rem; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:2.6em; }
        .mrm-tenant__meta{ color:#64748b; font-size:.9rem; }
        .mrm-tenant__price{ color:#0b3aa4; font-weight:900; }
        .mrm-tenant__specs{ color:#475569; font-size:.9rem; }

        /* FAQ */
        .mrm-tenant__faq{ display:grid; grid-template-columns:1fr; gap:.7rem; }
        @media (min-width: 900px){ .mrm-tenant__faq{ grid-template-columns:1fr 1fr; } }
        .mrm-tenant details{ background:#fff; border:1px solid var(--line); border-radius:12px; padding:.7rem 1rem; }
        .mrm-tenant summary{ cursor:pointer; font-weight:900; list-style:none; }
        .mrm-tenant summary::-webkit-details-marker{ display:none; }

        /* FINAL CTA */
        .mrm-tenant__final{ background:#fff; border:1px solid var(--line); border-radius:18px; padding: clamp(1.2rem, 4vw, 2rem); display:grid; grid-template-columns:1fr; gap:1rem; align-items:center; }
        @media (min-width:900px){ .mrm-tenant__final{ grid-template-columns:2fr 1fr; } }

        /* Buttons (light + outline variants) */
        .btn{ display:inline-flex; align-items:center; justify-content:center; font-weight:900; border-radius:999px; padding:.85rem 1.2rem; border:1px solid var(--line); cursor:pointer; transition:.2s transform,.25s box-shadow,.2s background; text-decoration:none; }
        .btn:hover{ transform: translateY(-1px); box-shadow:0 10px 22px rgba(2,6,23,.12); }
        .btn--primary{ background: var(--b); color:#fff; border-color: var(--b); }
        .btn--outline{ background: transparent; color:#fff; border-color: rgba(255,255,255,.7); }
        .btn--ghost{ background: transparent; color: var(--b2); }

        /* Utilities */
        .muted{ color:#64748b; font-size:.9rem; }
        .divider{ height:1px; background: var(--line); margin: 1rem 0; }

        /* Back to top */
        .mrm-tenant__top{ position:fixed; right:18px; bottom:18px; border:none; background:var(--b2); color:#fff; width:44px; height:44px; border-radius:50%; cursor:pointer; box-shadow:0 10px 22px rgba(2,6,23,.25); font-weight:900; }
        .mrm-tenant__top:hover{ transform: translateY(-1px); }

        /* skeleton */
        @keyframes shimmer { 0%{background-position:-450px 0} 100%{background-position:450px 0} }
      `}</style>

      <main className="mrm-tenant">
        {/* HERO */}
        <header className="mrm-tenant__hero" style={{ ['--hero']: `url('${heroImage}')` }}>
          <div className="mrm-tenant__container">
            <div className="mrm-tenant__crumb">
              <a href="/">Home</a> &rsaquo; Tenants
            </div>
            <h1 className="mrm-tenant__title">
              Rent with confidence. <span style={{ color: '#93c5fd' }}>We’ll look after you.</span>
            </h1>
            <p className="mrm-tenant__lede">
              One dedicated Middlesbrough office, local experts, deep neighbourhood knowledge. From viewings to maintenance, we’ll make renting simple and stress-free.
            </p>
            <div className="mrm-tenant__cta">
              <a href="/properties?types=rent" className="mrm-tenant__btn mrm-tenant__btn--primary">Browse homes to rent →</a>
              <a href="/repairs" className="mrm-tenant__btn mrm-tenant__btn--outline">Report a repair</a>
            </div>
            <div className="mrm-tenant__chips" role="list">
              <span className="mrm-tenant__chip" role="listitem">Priority alerts — 24h early access</span>
              <span className="mrm-tenant__chip" role="listitem">Deposit protected</span>
              <span className="mrm-tenant__chip" role="listitem">24/7 support</span>
            </div>
          </div>
        </header>

        {/* SERVICES */}
        <section className="mrm-tenant__section">
          <div className="mrm-tenant__container">
            <h2 className="mrm-tenant__heading">Everything you need, in one place</h2>
            <p className="mrm-tenant__sub">Manage your tenancy, pay rent securely and get help fast — online and on your phone.</p>

            <div className="mrm-tenant__grid">
              {services.map((s) => (
                <article key={s.title} className="mrm-tenant__card">
                  <div className="mrm-tenant__icon" aria-hidden="true">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                  <a className="cta" href={s.href}>{s.cta} →</a>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* STEPS */}
        <section className="mrm-tenant__section mrm-tenant__section--alt">
          <div className="mrm-tenant__container">
            <h2 className="mrm-tenant__heading">Your renting journey</h2>
            <p className="mrm-tenant__sub">Clear steps, friendly guidance and no jargon.</p>

            <div className="mrm-tenant__steps">
              {steps.map((s) => (
                <article key={s.n} className="mrm-tenant__step">
                  <div className="mrm-tenant__num">{s.n}</div>
                  <div>
                    <h4>{s.t}</h4>
                    <p>{s.d}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* INFO BAND */}
        <section className="mrm-tenant__section">
          <div className="mrm-tenant__container">
            <div className="mrm-tenant__band" aria-label="Tenant information">
              <h3>Right to Rent, deposit protection & fair fees</h3>
              <p>
                We follow best practice across referencing, tenancy agreements and deposit protection. You’ll receive prescribed information and clear statements throughout your tenancy.
              </p>
              <div className="mrm-tenant__row">
                <a href="/tenants/guide" className="btn btn--primary">Download Tenant Guide</a>
                <a href="/privacy" className="btn btn--outline">Privacy & GDPR</a>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED RENTALS */}
        <section className="mrm-tenant__section mrm-tenant__section--alt" aria-live="polite">
          <div className="mrm-tenant__container">
            <h2 className="mrm-tenant__heading">Featured to-let properties</h2>
            <p className="mrm-tenant__sub">A selection of the latest rentals across Teesside.</p>

            {!loading && rentals.length === 0 && (
              <div className="muted">No properties to show right now. Try again later or browse all rentals.</div>
            )}

            {loading ? (
              <div className="mrm-tenant__props">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="mrm-tenant__prop" aria-busy="true">
                    <div className="mrm-tenant__thumb" style={{ background: 'linear-gradient(90deg,#e5e7eb 25%,#f3f4f6 37%,#e5e7eb 63%)', backgroundSize: '900px 100%', animation: 'shimmer 1.2s infinite' }} />
                    <div className="mrm-tenant__body">
                      <div style={{ height: '12px', background: '#e5e7eb', borderRadius: '6px', width: '80%' }} />
                      <div style={{ height: '12px', background: '#e5e7eb', borderRadius: '6px', width: '60%' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mrm-tenant__props">
                {rentals.map((p) => {
                  const img = p.full_image_url || p.image_url || PLACEHOLDER;
                  const href = p.href || (p.slug ? `/property/${p.slug}` : '#');
                  const meta = [p.town, p.postcode].filter(Boolean).join(' · ');
                  const priceText = fmtGBP(p.price, p.price_text) + (p.price_text ? '' : ' pcm');
                  return (
                    <a key={p.id} className="mrm-tenant__prop" href={href}>
                      <div className="mrm-tenant__thumb">
                        <img
                          src={img}
                          alt={p.title}
                          loading="lazy"
                          onError={(e) => {
                            if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER;
                          }}
                        />
                        {p.listing_type && <span className="mrm-tenant__pill">{String(p.listing_type).toUpperCase()}</span>}
                      </div>
                      <div className="mrm-tenant__body">
                        <h3 className="mrm-tenant__h">{p.title}</h3>
                        <div className="mrm-tenant__meta">{meta}</div>
                        <div className="mrm-tenant__price">{priceText}</div>
                        <div className="mrm-tenant__specs">🛏 {p.beds ?? 0} · 🛁 {p.baths ?? 1}</div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}

            <div className="mrm-tenant__row" style={{ marginTop: '1rem' }}>
              <a href="/properties?types=rent" className="btn btn--ghost">View all rentals →</a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mrm-tenant__section">
          <div className="mrm-tenant__container">
            <h2 className="mrm-tenant__heading">Tenant FAQs</h2>
            <div className="mrm-tenant__sub">Quick answers to common questions — and we’re here if you need more help.</div>

            <div className="mrm-tenant__faq">
              {[
                ['How do I report a repair?', 'Use our online repairs form 24/7 via the “Report a repair” link. For emergencies, call the number in your welcome pack.'],
                ['Where is my deposit held?', 'Your deposit is protected in a government-approved scheme. You’ll receive the prescribed information and reference.'],
                ['Can I keep a pet?', 'Some landlords consider pets — ask us and we’ll check the property’s policy and any additional terms.'],
                ['What happens if my rent is late?', 'Please contact us immediately. We’ll help you get back on track and explain any arrears process.'],
                ['How much notice do I need to give?', 'This depends on your tenancy agreement. We’ll check your dates and guide you through the steps.'],
              ].map(([q, a]) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <div style={{ marginTop: '.4rem', color: '#475569' }}>{a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mrm-tenant__section mrm-tenant__section--alt">
          <div className="mrm-tenant__container">
            <div className="mrm-tenant__final">
              <div>
                <h3 className="mrm-tenant__heading" style={{ marginBottom: '.25rem' }}>Ready to find your next home?</h3>
                <p className="mrm-tenant__sub" style={{ marginTop: 0 }}>
                  Register for rental alerts and be the first to view new properties.
                </p>
              </div>
              <div className="mrm-tenant__row" style={{ justifyContent: 'flex-end' }}>
                <a href="/tenants/alerts" className="btn btn--primary">Register for alerts</a>
                <a href="/properties?types=rent" className="btn">Browse rentals</a>
              </div>
            </div>
            <div className="divider"></div>
            <div className="muted">*Information is guidance only and may vary by property and tenancy type.</div>
          </div>
        </section>
      </main>

      {/* Back to top */}
      <button
        className="mrm-tenant__top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        title="Back to top"
      >
        ↑
      </button>
    </>
  );
}
